import { lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { useAnalytics } from "@/hooks/useAnalytics";

const AnalyticsTracker = () => {
  useAnalytics();
  return null;
};

// Eagerly loaded — landing page & layout-critical
import HomePage from "./pages/HomePage";

// Lazy-loaded — not needed on initial page load
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AdminDocumentsPage = lazy(() => import("./pages/AdminDocumentsPage"));
const AdvancedAnalyticsPage = lazy(() => import("./pages/AdvancedAnalyticsPage"));
const ProfileSettingsPage = lazy(() => import("./pages/ProfileSettingsPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const NewsArticlePage = lazy(() => import("./pages/NewsArticlePage"));
const EventPage = lazy(() => import("./pages/EventPage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));
const OurServicesPage = lazy(() => import("./pages/OurServicesPage"));
const MinibusServicePage = lazy(() => import("./pages/MinibusServicePage"));
const VolunteerDriversPage = lazy(() => import("./pages/VolunteerDriversPage"));
const HomeVisitingPage = lazy(() => import("./pages/HomeVisitingPage"));
const GetInvolvedPage = lazy(() => import("./pages/GetInvolvedPage"));
const VolunteeringPage = lazy(() => import("./pages/VolunteeringPage"));
const VolunteerResourcesPage = lazy(() => import("./pages/VolunteerResourcesPage"));
const DonationsPage = lazy(() => import("./pages/DonationsPage"));
const LegaciesPage = lazy(() => import("./pages/LegaciesPage"));
const PoliciesPage = lazy(() => import("./pages/PoliciesPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AccessibilityProvider>
        <AuthProvider>
          <ScrollToTop />
          <AnalyticsTracker />
          <CookieConsentBanner />
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<OurServicesPage />} />
            <Route path="/services/minibus" element={<MinibusServicePage />} />
            <Route path="/services/volunteer-drivers" element={<VolunteerDriversPage />} />
            <Route path="/services/home-visiting" element={<HomeVisitingPage />} />
            <Route path="/get-involved" element={<GetInvolvedPage />} />
            <Route path="/get-involved/volunteering" element={<VolunteeringPage />} />
            <Route path="/volunteers/resources" element={<VolunteerResourcesPage />} />
            <Route path="/get-involved/donations" element={<DonationsPage />} />
            <Route path="/get-involved/legacies" element={<LegaciesPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/events/:id" element={<EventPage />} />
            <Route path="/news/:id" element={<NewsArticlePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route
              path="/portal/admin"
              element={
                <ProtectedRoute allowedRoles={['web_manager', 'admin', 'content_admin']}>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/admin/documents"
              element={
                <ProtectedRoute allowedRoles={['web_manager', 'admin', 'content_admin']}>
                  <AdminDocumentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portal/admin/advanced-analytics"
              element={
                <ProtectedRoute allowedRoles={['web_manager']}>
                  <AdvancedAnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/settings"
              element={
                <ProtectedRoute allowedRoles={['web_manager', 'admin', 'content_admin']}>
                  <ProfileSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AuthProvider>
        </AccessibilityProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
